import { colors as themeColors } from '../utils/theme';
import { useEffect, useRef, useState, startTransition } from "react";
import { Box, CircularProgress } from "@mui/material";
import Schedule from "@mui/icons-material/Schedule";
import DateRange from "@mui/icons-material/DateRange";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import Timeline from "@mui/icons-material/Timeline";
import { colors, gradients } from "../utils/theme";

// Shared premium segmented control for Today / Weekly / Monthly / Yearly.
// Used by the dashboard header (size="md") and the Top Clients / Products / Employees cards (size="sm").
//
// Animation notes
//  - ONE sliding pill that only animates `transform` (compositor-friendly, no layout/paint work).
//  - The pill follows LOCAL state so it starts moving on the next frame; the parent's heavy update is
//    scheduled as a low-priority transition so it never blocks the slide.
//  - Web Animations API gives an elastic stretch mid-trip. Windows reports "reduced motion" in Remote
//    Desktop sessions, so in that case a short calm slide is still played instead of teleporting.

export const PERIODS = ["today", "weekly", "monthly", "yearly"];
const ICONS = { today: Schedule, weekly: DateRange, monthly: CalendarMonth, yearly: Timeline };

const SIZES = {
  md: { pad: 5, h: 40, trackR: "16px", pillR: "12px", font: { xs: "0.76rem", sm: "0.85rem" }, icon: 16, width: { xs: "100%", sm: 460 } },
  sm: { pad: 4, h: 32, trackR: "12px", pillR: "9px",  font: { xs: "0.72rem", sm: "0.76rem" }, icon: 14, width: "100%" },
};

const PeriodToggle = ({ value, onChange, loading = false, size = "md" }) => {
  const S = SIZES[size] || SIZES.md;
  const [shown, setShown] = useState(value);
  useEffect(() => { setShown(value); }, [value]);
  const pillRef = useRef(null);

  const select = (p) => {
    if (p === shown) return;
    const from = PERIODS.indexOf(shown), to = PERIODS.indexOf(p);
    const el = pillRef.current;
    if (el && el.animate) {
      const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const stretch = calm ? 1 : 1 + 0.16 * Math.min(Math.abs(to - from), 3);
      el.animate(
        [
          { transform: `translateX(${from * 100}%) scaleX(1) scaleY(1)`, offset: 0 },
          { transform: `translateX(${((from + to) / 2) * 100}%) scaleX(${stretch}) scaleY(0.94)`, offset: 0.45 },
          { transform: `translateX(${to * 100}%) scaleX(1) scaleY(1)`, offset: 1 },
        ],
        calm ? { duration: 240, easing: "cubic-bezier(0.25, 0.8, 0.3, 1)" } : { duration: 520, easing: "cubic-bezier(0.45, 0, 0.2, 1)" }
      );
    }
    setShown(p);
    startTransition(() => { if (onChange) onChange(p); });
  };

  const idx = Math.max(0, PERIODS.indexOf(shown));

  return (
    <Box
      role="tablist"
      aria-label="Period"
      sx={{
        position: "relative", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", p: `${S.pad}px`,
        background: "linear-gradient(180deg, #EEF2F8 0%, #E6ECF5 100%)",
        border: "1px solid rgba(255,255,255,0.9)", borderRadius: S.trackR,
        boxShadow: "inset 0 1px 3px rgba(15,23,42,0.10), 0 6px 18px rgba(15,23,42,0.05)",
        width: S.width, maxWidth: "100%",
      }}
    >
      {/* sliding pill */}
      <Box
        ref={pillRef}
        aria-hidden
        sx={{
          position: "absolute", top: `${S.pad}px`, bottom: `${S.pad}px`, left: `${S.pad}px`, width: `calc((100% - ${S.pad * 2}px) / 4)`,
          borderRadius: S.pillR, overflow: "hidden", background: gradients.primary,
          boxShadow: "0 8px 20px rgba(24,119,242,0.38), inset 0 1px 0 rgba(255,255,255,0.35)",
          transform: `translateX(${idx * 100}%)`, transformOrigin: "center", willChange: "transform",
          "&::after": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 55%)" },
        }}
      />
      {PERIODS.map((p) => {
        const active = p === shown;
        const Icon = ICONS[p];
        return (
          <Box
            key={p}
            component="button"
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => select(p)}
            sx={{
              position: "relative", zIndex: 1, border: 0, background: "transparent", cursor: "pointer", outline: "none", userSelect: "none", WebkitUserSelect: "none",
              height: S.h, px: { xs: 0.5, sm: 1.2 }, borderRadius: S.pillR,
              display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: 0.4, sm: 0.7 },
              fontFamily: "inherit", fontSize: S.font, fontWeight: active ? 700 : 600, letterSpacing: "0.1px",
              color: active ? themeColors.neutral.surface : colors.neutral.sub,
              textShadow: active ? "0 1px 2px rgba(8,85,168,0.35)" : "none",
              transition: "color 260ms ease",
              WebkitTapHighlightColor: "transparent",
              "& .period-icon": { fontSize: S.icon, transition: "transform 320ms cubic-bezier(0.34, 1.25, 0.64, 1), opacity 200ms ease", opacity: active ? 1 : 0.65, transform: active ? "scale(1.12)" : "scale(1)", display: { xs: "none", sm: "block" } },
              "&:hover": { color: active ? themeColors.neutral.surface : colors.neutral.ink, "& .period-icon": { opacity: 1 } },
              "&:focus-visible": { boxShadow: "0 0 0 3px rgba(24,119,242,0.40)" },
              "&:active .period-label": { transform: "scale(0.96)" },
              "& .period-label": { transition: "transform 160ms ease" },
            }}
          >
            {active && loading
              ? <CircularProgress size={S.icon - 2} thickness={5} sx={{ color: themeColors.neutral.surface }} />
              : <Icon className="period-icon" />}
            <span className="period-label">{p === "today" ? "Today" : p.charAt(0).toUpperCase() + p.slice(1)}</span>
          </Box>
        );
      })}
    </Box>
  );
};

export default PeriodToggle;
